<template>
  <div>
    <!-- =========================
         YOUTUBE URL + SYNC
    ========================== -->
    <div class="mb-3">
      <label class="form-label">
        YouTube URL <span class="text-danger">*</span>
      </label>

      <div class="input-group">
        <input
          v-model="form.youtube_url"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.youtube_url }"
          placeholder="https://www.youtube.com/watch?v=xxxx"
        />

        <button
          class="btn btn-outline-primary"
          type="button"
          :disabled="syncing || !form.youtube_url"
          @click="syncYoutube"
        >
          <i
            class="mdi"
            :class="syncing ? 'mdi-loading mdi-spin' : 'mdi-sync'"
          ></i>
          Sync
        </button>
      </div>

      <div class="invalid-feedback d-block" v-if="errors.youtube_url">
        {{ errors.youtube_url }}
      </div>

      <small class="text-muted">
        Paste URL YouTube lalu klik Sync
      </small>
    </div>

    <!-- =========================
         THUMBNAIL PREVIEW
    ========================== -->
    <div v-if="preview.thumbnail" class="mb-3">
      <label class="form-label">Preview</label>
      <img
        :src="preview.thumbnail"
        class="img-fluid rounded border"
        style="max-height: 220px"
      />
    </div>

    <!-- =========================
         TITLE
    ========================== -->
    <div class="mb-3">
      <label class="form-label">
        Judul Video <span class="text-danger">*</span>
      </label>
      <input
        v-model="form.title"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': errors.title }"
        placeholder="Judul otomatis dari YouTube"
      />
      <div class="invalid-feedback">
        {{ errors.title }}
      </div>
    </div>

    <!-- =========================
         CATEGORY
    ========================== -->
    <div class="mb-3">
      <label class="form-label">
        Kategori <span class="text-danger">*</span>
      </label>
      <select
        v-model="form.category_id"
        class="form-select"
        :class="{ 'is-invalid': errors.category_id }"
      >
        <option value="">-- Pilih Kategori --</option>
        <option
          v-for="c in categories"
          :key="c.id"
          :value="c.id"
        >
          {{ c.name }}
        </option>
      </select>
      <div class="invalid-feedback">
        {{ errors.category_id }}
      </div>
    </div>

    <!-- =========================
         DESCRIPTION
    ========================== -->
    <div class="mb-3">
      <label class="form-label">Deskripsi</label>
      <textarea
        v-model="form.description"
        class="form-control"
        rows="5"
        placeholder="Deskripsi otomatis dari YouTube"
      ></textarea>
    </div>

    <!-- =========================
         ACTION
    ========================== -->
    <div class="text-end">
      <button
        class="btn btn-secondary me-2"
        @click="$emit('cancel')"
      >
        Batal
      </button>

      <button
        class="btn btn-success"
        :disabled="saving"
        @click="submit"
      >
        <i v-if="saving" class="mdi mdi-loading mdi-spin"></i>
        Simpan
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "FormVideo",

  props: {
    id: {
      type: [Number, null],
      default: null,
    },
  },

  data() {
    return {
      syncing: false,
      saving: false,

      form: {
        title: "",
        description: "",
        youtube_url: "",
        youtube_video_id: "",
        category_id: "",
      },

      preview: {
        thumbnail: null,
      },

      categories: [],
      errors: {},
    };
  },

  mounted() {
    this.fetchCategories();

    if (this.id) {
      this.fetchDetail();
    }
  },

  methods: {
    /* =========================
       AUTH HEADER
    ========================== */
    authHeader() {
      return {
        Authorization: "Bearer " + localStorage.access_token,
      };
    },

    /* =========================
       VALIDASI FE
    ========================== */
    validateForm() {
      this.errors = {};

      if (!this.form.youtube_url) {
        this.errors.youtube_url = "URL YouTube wajib diisi";
      }

      if (!this.form.title) {
        this.errors.title = "Judul video wajib diisi";
      }

      if (!this.form.category_id) {
        this.errors.category_id = "Kategori wajib dipilih";
      }

      return Object.keys(this.errors).length === 0;
    },

    /* =========================
       SYNC YOUTUBE
    ========================== */
    async syncYoutube() {
      if (!this.form.youtube_url) {
        this.errors.youtube_url = "URL YouTube wajib diisi";
        return;
      }

      this.syncing = true;

      try {
        const res = await axios.post(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master/kms/video/sync-youtube",
          { youtube_url: this.form.youtube_url },
          { headers: this.authHeader() }
        );

        const d = res.data.data;

        this.form.title = d.title;
        this.form.description = d.description;
        this.form.youtube_video_id = d.youtube_video_id;
        this.preview.thumbnail = d.thumbnail;

        Swal.fire(
          "Berhasil",
          "Data video berhasil disinkronkan dari YouTube",
          "success"
        );
      } catch (err) {
        Swal.fire(
          "Gagal",
          err.response?.data?.message || "Gagal sync YouTube",
          "error"
        );
      } finally {
        this.syncing = false;
      }
    },

    /* =========================
       SUBMIT FORM
    ========================== */
    async submit() {
      if (!this.validateForm()) {
        Swal.fire(
          "Data belum lengkap",
          "Silakan lengkapi semua field yang wajib diisi",
          "warning"
        );
        return;
      }

      this.saving = true;

      try {
        const url = this.id
          ? `master/kms/video/${this.id}`
          : "master/kms/video";

        const method = this.id ? "put" : "post";

        await axios[method](
          process.env.VUE_APP_BACKEND_URL_VERSION + url,
          this.form,
          { headers: this.authHeader() }
        );

        Swal.fire("Sukses", "Video berhasil disimpan", "success");
        this.$emit("success");
      } catch (err) {
        Swal.fire(
          "Gagal",
          err.response?.data?.message || "Gagal menyimpan video",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },

    /* =========================
       FETCH CATEGORY
    ========================== */
    fetchCategories() {
      axios
        .get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master/kms/video/kategori",
          { headers: this.authHeader() }
        )
        .then(res => {
          this.categories = res.data.data || [];
        });
    },

    /* =========================
       FETCH DETAIL (EDIT)
    ========================== */
    fetchDetail() {
      axios
        .get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master/kms/video/" +
            this.id,
          { headers: this.authHeader() }
        )
        .then(res => {
          Object.assign(this.form, res.data.data);

          if (res.data.data.youtube_video_id) {
            this.preview.thumbnail =
              "https://img.youtube.com/vi/" +
              res.data.data.youtube_video_id +
              "/mqdefault.jpg";
          }
        });
    },
  },
};
</script>
